import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import BarChart4BarsIcon from '@/material-icons/400-24px/bar_chart_4_bars.svg?react';
import PhotoLibraryIcon from '@/material-icons/400-24px/photo_library.svg?react';
import { Avatar } from 'mastodon/components/avatar';
import { DisplayName } from 'mastodon/components/display_name';
import { Icon } from 'mastodon/components/icon';
import { EmbeddedStatusContent } from 'mastodon/features/notifications_v2/components/embedded_status_content';

export const ReplyIndicator = () => {
  const scrollRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const inReplyToId = useSelector(state => state.getIn(['compose', 'in_reply_to']));
  const accounts = useSelector(state => state.get('accounts'));

  // Redux 스토어에 캐시된 타래를 안전하게 역추적
  const thread = useSelector(state => {
    let currentId = inReplyToId;
    const statuses = [];
    const visited = new Set();

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const st = state.getIn(['statuses', currentId]);
      if (!st) break;
      statuses.unshift(st); 
      currentId = st.get('in_reply_to_id'); 
    }
    return statuses;
  });

  useEffect(() => {
    setIsExpanded(false);
  }, [inReplyToId]);

  useEffect(() => {
    if (isExpanded && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [isExpanded, thread]);

  if (!thread || thread.length === 0) {
    return null;
  }

  const renderStatus = (status, isLast) => {
    if (!status || typeof status.get !== 'function') return null;

    const accountId = status.get('account');
    const account = accounts.get(accountId);

    if (!account) return null;

    const poll = status.get('poll');
    const mediaAttachments = status.get('media_attachments');
    const hasMedia = mediaAttachments && typeof mediaAttachments.size === 'number' && mediaAttachments.size > 0;

    return (
      <div 
        key={status.get('id')} 
        className='reply-indicator' 
        style={{ 
          borderBottom: isLast ? 'none' : '1px dashed var(--color-border-primary)', 
          paddingBottom: isLast ? '0' : '10px', 
          marginBottom: isLast ? '0' : '10px' 
        }}
      >
        <div className='reply-indicator__line' />
        <Link to={`/@${account.get('acct')}`} className='detailed-status__display-avatar'>
          <Avatar key={`avatar-${account.get('id')}`} account={account} size={46} />
        </Link>
        <div className='reply-indicator__main'>
          <Link to={`/@${account.get('acct')}`} className='detailed-status__display-name'>
            <DisplayName account={account} />
          </Link>
          <EmbeddedStatusContent className='reply-indicator__content translate' status={status} />
          {(poll || hasMedia) && (
            <div className='reply-indicator__attachments'>
              {poll && <><Icon icon={BarChart4BarsIcon} /><FormattedMessage id='reply_indicator.poll' defaultMessage='Poll' /></>}
              {hasMedia && <><Icon icon={PhotoLibraryIcon} /><FormattedMessage id='reply_indicator.attachments' defaultMessage='{count, plural, one {# attachment} other {# attachments}}' values={{ count: mediaAttachments.size }} /></>}
            </div>
          )}
        </div>
      </div>
    );
  };

  const lastStatus = thread[thread.length - 1];

  return (
    <div style={{ marginBottom: '10px' }}>
      {thread.length > 1 && !isExpanded && (
        <button 
          type="button"
          onClick={() => setIsExpanded(true)}
          style={{
            width: '100%',
            padding: '8px',
            marginBottom: '10px',
            background: 'var(--color-bg-secondary)',
            color: 'var(--color-text-secondary)',
            border: '1px solid var(--color-border-primary)',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 'bold',
            transition: 'background 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.background = 'var(--color-bg-primary)'}
          onMouseOut={(e) => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
        >
          이전 대화 보기 ({thread.length - 1}개)
        </button>
      )}

      {!isExpanded ? (
        renderStatus(lastStatus, true)
      ) : (
        <div 
          ref={scrollRef} 
          className='reply-indicator-thread dropdown-animation' 
          style={{ display: 'flex', flexDirection: 'column', maxHeight: '300px', overflowY: 'auto' }}
        >
          {thread.map((status, index) => renderStatus(status, index === thread.length - 1))}
        </div>
      )}
    </div>
  );
};